import React from 'react';
import {
  Card,
  CardBody,
  CardTitle,
  Content,
  Grid,
  GridItem,
  Skeleton,
  Title,
} from '@patternfly/react-core';
import { formatBytes } from '../../../utils/formatBytes';
import { formatWallClockDateTime } from '../../../utils/dateFormat';
import type { BackupInventorySummary } from '../../../types/models/Backup';

export interface BackupSummaryCardsProps {
  summary: BackupInventorySummary | null;
  loading: boolean;
  error: Error | null;
}

/** One card: a headline figure with a quieter line of context underneath. */
const SummaryCard: React.FunctionComponent<{ title: string; children: React.ReactNode; detail: React.ReactNode }> = ({
  title,
  children,
  detail,
}) => (
  <Card isCompact isFullHeight>
    <CardTitle>{title}</CardTitle>
    <CardBody>
      <Title headingLevel="h3" size="2xl">
        {children}
      </Title>
      <Content component="small">{detail}</Content>
    </CardBody>
  </Card>
);

/**
 * The four figures above the backups table, from `GET /datacenters/backups/summary`.
 *
 * A tenant with nothing backed up gets zeros, `null` datetimes and an empty target list — that is
 * a **success**, not an error, and renders as a legitimate all-zero row.
 *
 * The summary is also the only published source of the staleness threshold: the backend echoes
 * `stale_after_hours` precisely so the frontend never hardcodes it, so the Attention card names
 * the value it was given rather than a literal.
 */
const BackupSummaryCards: React.FunctionComponent<BackupSummaryCardsProps> = ({ summary, loading, error }) => {
  if (error) {
    // The table below is the page's real content and loads independently — a failed summary
    // costs the reader four figures, so it is reported quietly rather than as a page failure.
    return (
      <Content component="p" className="pf-v6-u-mb-md">
        Summary figures are unavailable: {error.message}
      </Content>
    );
  }

  if (loading || summary === null) {
    return (
      <Grid hasGutter className="pf-v6-u-mb-md">
        {[0, 1, 2, 3].map((i) => (
          <GridItem key={i} span={12} md={6} lg={3}>
            <Card isCompact isFullHeight>
              <CardBody>
                <Skeleton screenreaderText="Loading backup summary" />
              </CardBody>
            </Card>
          </GridItem>
        ))}
      </Grid>
    );
  }

  const attentionCount = summary.stale_count + summary.missing_count;

  return (
    <Grid hasGutter className="pf-v6-u-mb-md">
      <GridItem span={12} md={6} lg={3}>
        <SummaryCard
          title="VMs backed up"
          detail={
            summary.retired_count > 0
              ? `${summary.retired_count} retired (kept on purpose)`
              : 'None retired'
          }
        >
          {summary.vm_count}
        </SummaryCard>
      </GridItem>

      <GridItem span={12} md={6} lg={3}>
        {/* Allocated can be smaller than apparent (sparse images) or marginally larger (block
            rounding), so the two are shown side by side rather than as a bounded ratio. */}
        <SummaryCard title="Total size" detail={`${formatBytes(summary.allocated_bytes)} allocated on disk`}>
          {formatBytes(summary.size_bytes)}
        </SummaryCard>
      </GridItem>

      <GridItem span={12} md={6} lg={3}>
        {/* A retired VM's old backup is excluded from this figure by the backend, so a
            decommissioned machine cannot make the card look fresh. */}
        <SummaryCard title="Latest backup" detail={`Figures as of ${formatWallClockDateTime(summary.as_of)}`}>
          {formatWallClockDateTime(summary.last_backup_at)}
        </SummaryCard>
      </GridItem>

      <GridItem span={12} md={6} lg={3}>
        <SummaryCard
          title="Attention"
          detail={
            attentionCount === 0
              ? `No backup older than ${summary.stale_after_hours}h, nothing missing`
              : `${summary.stale_count} stale (over ${summary.stale_after_hours}h), ${summary.missing_count} missing`
          }
        >
          {attentionCount}
        </SummaryCard>
      </GridItem>
    </Grid>
  );
};

export default BackupSummaryCards;
